import { SortOrder } from "@src/__generated__/graphql";
import type {
  Column,
  ColumnDef,
  ExpandedState,
  RowSelectionState,
  Row as TanstackRowType,
  Updater,
} from "@tanstack/react-table";
import type { CSSProperties } from "react";
import type { BodyColumnMeta, BodyRow } from "../types";

export function resolveColumnId<Row extends BodyRow>(
  column: ColumnDef<Row, unknown>,
  index: number,
): string {
  const withId = column as ColumnDef<Row, unknown> & {
    id?: string;
    accessorKey?: string;
  };

  if (withId.id) {
    return withId.id;
  }

  if (withId.accessorKey) {
    return withId.accessorKey;
  }

  return `column_${index}`;
}

export function resolveColumnLabel<Row extends BodyRow>(
  column: ColumnDef<Row, unknown>,
): string {
  const meta = getColumnMeta(column);

  if (meta?.label) {
    return meta.label;
  }

  if (typeof column.header === "string") {
    return column.header;
  }

  const withAccessorKey = column as ColumnDef<Row, unknown> & {
    accessorKey?: string;
  };

  if (withAccessorKey.accessorKey) {
    return withAccessorKey.accessorKey;
  }

  return resolveColumnId(column, 0);
}

export function getColumnMeta<Row extends BodyRow>(
  column: ColumnDef<Row, unknown>,
): BodyColumnMeta | undefined {
  if (!column.meta) {
    return undefined;
  }

  return column.meta as BodyColumnMeta;
}

export function flattenRowIds<Row extends BodyRow>(rows: Row[]): string[] {
  return rows.reduce<string[]>((acc, row) => {
    acc.push(row.id);

    if (row.__subRows?.length) {
      acc.push(...flattenRowIds(row.__subRows as Row[]));
    }

    return acc;
  }, []);
}

export function resolveUpdater<T>(updater: Updater<T>, current: T): T {
  if (typeof updater === "function") {
    return (updater as (old: T) => T)(current);
  }

  return updater;
}

export function isSameStringArray(left: string[], right: string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((item, index) => item === right[index]);
}

export function isSameRecord<T extends Record<string, unknown>>(
  left: T,
  right: T,
): boolean {
  const leftEntries = Object.entries(left);
  const rightEntries = Object.entries(right);

  if (leftEntries.length !== rightEntries.length) {
    return false;
  }

  return leftEntries.every(([key, value]) => right[key] === value);
}

const SIZE_CSS_VAR_SANITIZE_PATTERN = /[^\w-]/g;

export function sanitizeColumnSizeId(id: string): string {
  return id.replace(SIZE_CSS_VAR_SANITIZE_PATTERN, "_");
}

export function getHeaderSizeCssVar(headerId: string): string {
  return `--header-${sanitizeColumnSizeId(headerId)}-size`;
}

export function getColumnSizeCssVar(columnId: string): string {
  return `--col-${sanitizeColumnSizeId(columnId)}-size`;
}

export function getNextOrder(
  current: SortOrder | undefined,
): SortOrder | undefined {
  return current === SortOrder.Desc
    ? SortOrder.Asc
    : current === SortOrder.Asc
      ? undefined
      : SortOrder.Desc;
}

export function idsToSelectionState(
  ids: string[] | Set<string>,
): RowSelectionState {
  const normalizedIds = Array.from(ids);

  if (normalizedIds.length === 0) {
    return {};
  }

  return normalizedIds.reduce<RowSelectionState>((acc, id) => {
    acc[id] = true;
    return acc;
  }, {});
}

export function getTableRowId<Row extends BodyRow>(
  row: Row,
  index: number,
  parent?: TanstackRowType<Row>,
): string {
  if (!parent) {
    return row.id;
  }

  return `${parent.id}.${index}.${row.id}`;
}

export function getBooleanRecordStateKey(
  state: ExpandedState | RowSelectionState,
): string {
  if (typeof state === "boolean") {
    return String(state);
  }

  return Object.keys(state)
    .toSorted()
    .map((key) => `${key}:${state[key]}`)
    .join("|");
}

export function getRowInteractionKey<Row extends BodyRow>(
  row: TanstackRowType<Row>,
): string {
  return [
    row.getIsExpanded(),
    row.getIsSelected(),
    row.getIsSomeSelected(),
    row.getCanSelect(),
    row.getCanExpand(),
  ].join("|");
}

export function getCommonPinningStyles<Row extends BodyRow>(
  column: Column<Row, unknown>,
): CSSProperties {
  const isPinned = column.getIsPinned();

  if (!isPinned) {
    return {};
  }

  const isLastLeftPinned = column.getIsLastColumn("left");

  return {
    boxShadow: isLastLeftPinned
      ? // eslint-disable-next-line lingui/no-unlocalized-strings
        "-4px 0 4px -4px rgba(0,0,0,0.24) inset"
      : undefined,
    position: "sticky",
    left: `${column.getStart("left")}px`,
    zIndex: 1,
  };
}
